'use client';

import { getCdnUrl } from '@morpho-org/dapp-core';
import { tween, tweenEasings } from '@morpho-org/ui-v6/animations';
import { useWindowResizeHandler } from '@morpho-org/ui-v6/hooks';
import { type SyntheticEvent, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useIsMobile } from '@/hooks/useBreakpoints';
import type { HowItWorksOverlayProps } from './HowItWorksOverlay';
export const useHowItWorksOverlay = ({
  steps
}: Pick<HowItWorksOverlayProps, 'steps'>) => {
  const isMobile = useIsMobile();
  const [animationStep, setAnimationStep] = useState<number>(0);
  const mobileStepsContainer = useRef<HTMLDivElement>(null);
  const mobileSteps = useRef<HTMLDivElement[]>([]);
  const mobileContainerHeight = useRef<number>(0);
  const startTouch = useRef<TouchEvent | null>(null);
  const animationContainer = useRef<HTMLDivElement | null>(null);
  const resizeTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mediaBase = getCdnUrl('v2/assets/animations-assets');
  const preventDoubleClickToSelect = useCallback((event: SyntheticEvent<HTMLElement, MouseEvent>) => {
    if (event.nativeEvent.detail > 1) event.preventDefault();
  }, []);
  const getMobileStepRef = useCallback((index: number) => (r: HTMLDivElement) => {
    if (mobileSteps.current) mobileSteps.current[index] = r;
  }, []);
  const resizeMobileStepsContainer = useCallback((event?: Event) => {
    if (!mobileStepsContainer.current) return;
    // Skip on desktop
    if (!isMobile) {
      mobileStepsContainer.current.style.height = '';
      return;
    }
    // Target current step and measure it
    const currentStep = mobileSteps.current[animationStep];
    if (!currentStep) return;
    const rect = currentStep.getBoundingClientRect();
    // Animate parent and update size
    tween(mobileContainerHeight, {
      current: rect.height
    }, {
      ease: tweenEasings.aggressiveInOut,
      duration: event ? 0 : 0.4,
      onUpdate() {
        if (!mobileStepsContainer.current) return;
        mobileStepsContainer.current.style.height = `${mobileContainerHeight.current}px`;
      }
    });
  }, [isMobile, animationStep]);
  const onTouchStep = useCallback((event: SyntheticEvent<HTMLDivElement, TouchEvent>) => {
    const {
      nativeEvent
    } = event;
    if (event.type === 'touchstart') {
      startTouch.current = nativeEvent;
      return;
    }
    const newTouch = nativeEvent.changedTouches.item(0);
    const oldTouch = startTouch.current?.changedTouches.item(0);
    if (!newTouch || !oldTouch) return;
    const deltaX = newTouch.clientX - oldTouch.clientX;
    const deltaY = newTouch.clientY - oldTouch.clientY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      let newStep = deltaX > 0 ? animationStep - 1 : animationStep + 1;
      newStep = Math.max(0, Math.min(newStep, steps.length - 1));
      setAnimationStep(newStep);
      event.preventDefault();
    }
  }, [animationStep, steps.length]);
  const centerAnimationInContainer = useCallback(() => {
    // Target animation container and it's animation child
    const parentElement = animationContainer.current;
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const childElement = parentElement?.firstChild as HTMLElement;
    if (!parentElement || !childElement) return;
    // Get 2 viewport rectangle to compte ratios
    const parentRect = parentElement.getBoundingClientRect();
    const childRect = childElement.getBoundingClientRect();
    // Compute ratio and deduce the align switch
    const parentRatio = parentRect.width / parentRect.height;
    const childRatio = childRect.width / childRect.height;
    const alignSwitch = parentRatio > childRatio;
    // Compute centering positions and apply them
    const x = alignSwitch ? (parentRect.width - childRect.width) / 2 : 0;
    const y = alignSwitch ? 0 : (parentRect.height - childRect.height) / 2;
    childElement.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }, []);
  useLayoutEffect(() => {
    resizeMobileStepsContainer();
    window.addEventListener('resize', resizeMobileStepsContainer);
    return () => window.removeEventListener('resize', resizeMobileStepsContainer);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [animationStep, isMobile]);
  useWindowResizeHandler(() => {
    centerAnimationInContainer();
    // This delayed refresh is for chrome iOS
    clearTimeout(resizeTimeout.current);
    resizeTimeout.current = setTimeout(centerAnimationInContainer, 300);
  });
  return {
    animationContainer,
    animationStep,
    mobileStepsContainer,
    mediaBase,
    getMobileStepRef,
    setAnimationStep,
    preventDoubleClickToSelect,
    onTouchStep
  };
};